<template>
  <pf-title h="3">{{ $t('Inviaci il tuo progetto di comarketing') }}</pf-title>
  <p>{{ $t("Scrivici l'idea e parteciperemo al tuo progetto") }}</p>

  <pf-empty-state v-if="sending">
    <pf-empty-state-header :title="$t('Attendere. Invio in corso...')" heading-level="h4">
      <template #icon>
        <pf-empty-state-icon>
          <pf-spinner />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <x-form v-else
    :submit-text=" $t('Invia la richiesta')"
    :prevent-submit="!attachments.length"
    @submit="send"
    style="max-width: 80ch"
  >
    <form-group :name="$t('Importo')">
      <x-input
        v-model="importo"
        name="importo"
        type="number"
        :max="total"
        step="0.01"
        :disabled="!enabled"
        :placeholder="$t('Importo')"
        required
      />
    </form-group>

    <form-group :name="$t('Descrizione')">
      <pf-textarea
        v-model="description"
        :disabled="!enabled"
        :placeholder="$t('Descrivi brevemente il tuo progetto')"
        rows="2"
      />
    </form-group>

    <div>{{ $t("Inviaci le fatture e le contabili di pagamento associate al progetto") }}</div>
    <form-group :name="$t('Allegati')">
      <pf-button :disabled="!enabled" component="label" variant="control" style="cursor: pointer" small>
        <template #icon>
          <paperclip-icon />
        </template>
        {{ $t('Sfoglia') }}
        <input
          type="file"
          ref="inputAttachments"
          name="attachments"
          :disabled="!enabled"
          style="display: none"
          @change="refreshAttachments"
        >
      </pf-button>

      {{ ' ' }}

      <template v-if="attachments.length" v-for="(file, index) in attachments" :key="index">
        <pf-label
          @close="attachments.splice(index, 1)"
        >
          <template #icon>
            <paperclip-icon />
          </template>
          {{ file.name }} ({{ $size(file.size) }})
        </pf-label>
      </template>
    </form-group>
  </x-form>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, useTemplateRef } from 'vue';
import { useAlertStore } from '@/store/alerts';
import { $t as _t } from '@/i18n';
import { CreditDocumentsResource } from '@/resources';
import PaperclipIcon from '@vue-patternfly/icons/paperclip-icon';
import { useFunctionRunningState } from '@common/use';

const props = defineProps<{
  total: number;
  currency: 'CHF' | 'EUR';
}>();

const emit = defineEmits<{
  (e: 'submit', event: Event|undefined): void
}>();

const alert = useAlertStore();

const importo = ref(props.total);
const description = ref('');
const attachments: Ref<File[]> = ref([]);
const inputAttachments = useTemplateRef('inputAttachments');

const enabled = computed(() => props.total > 0);

const { isRunning: sending, run: send } = useFunctionRunningState(async(e?: Event) => {
  if (!inputAttachments?.value?.files) {
    return;
  }

  await new CreditDocumentsResource().save({
    tipo: 'comarketing',
    messaggio: description.value,
    allegati: inputAttachments?.value?.files,
    importo: Number(importo.value),
  });

  description.value = '';
  attachments.value = [];

  alert.add({ title: _t('Richiesta inviata correttamente'), type: 'success', toast: true});
  emit('submit', e);
}, { preventMultiple: true });

function refreshAttachments() {
  if (!inputAttachments?.value?.files) {
    return;
  }

  for (const file of inputAttachments.value.files) {
    attachments.value.push(file);
  }
};

</script>
